/**
 * The confirmation for uninstalling a capability, shared by its card and its
 * page (Feature 87, D1). Cascading to auto-installed dependencies is opt-in.
 * From the page it may also delete the plugin from the index, once
 * uninstalled.
 */
import { useState } from 'react';

import { Button } from '../../ui/Button.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { Dialog, DialogActions } from '../../ui/Dialog.js';
import { Spinner } from '../../ui/Spinner.js';

export function UninstallConfirmDialog({
  open,
  onOpenChange,
  name,
  pending,
  onConfirm,
  deleteVersions,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  name: string;
  pending: boolean;
  onConfirm: (cascade: boolean) => void;
  /**
   * Set when the confirmation also deletes the plugin from the index
   * (Feature 87, D1): the number of versions that go with it.
   */
  deleteVersions?: number | undefined;
}) {
  // Default: do NOT cascade. Operator opts in by toggling the switch.
  // The state resets every time the dialog opens fresh.
  const [cascade, setCascade] = useState(false);
  const deleting = deleteVersions !== undefined;

  return (
    <Dialog
      open={open}
      onClose={() => {
        setCascade(false);
        onOpenChange(false);
      }}
      title={deleting ? <>Uninstall and delete {name}?</> : <>Uninstall {name}?</>}
      subtitle={
        <>
          Removes the plugin from <code className="mono">plugins-site</code> and the installed
          registry.{' '}
          {deleting
            ? `Then every published archive is removed from the index and all ${deleteVersions} version${deleteVersions === 1 ? '' : 's'} are dropped. This cannot be undone. `
            : null}
          Auto-installed <code className="mono">optum-*</code> dependencies are kept by default —
          turn on the toggle below to also drop deps that nothing else still requires.
        </>
      }
    >
      <label
        style={{
          marginTop: 16,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 11,
          borderRadius: 'var(--radius-control)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-sunken)',
          padding: 11,
        }}
      >
        <Checkbox
          checked={cascade}
          onChange={(checked) => setCascade(checked)}
          disabled={pending}
        />
        <span style={{ flex: '1 1 0%' }}>
          <span
            style={{
              display: 'block',
              fontSize: 'var(--text-meta-lg)',
              fontWeight: 'var(--weight-medium)',
            }}
          >
            Also remove auto-installed deps
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--text-muted)',
            }}
          >
            Walks <code className="mono">installed_via</code> descendants and drops each one no
            other installed plugin still requires.
          </span>
        </span>
      </label>
      <DialogActions>
        <Button
          type="button"
          variant="ghost"
          onClick={() => onOpenChange(false)}
          disabled={pending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="danger"
          onClick={() => onConfirm(cascade)}
          disabled={pending}
        >
          {pending ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <Spinner />
              {deleting ? 'Deleting' : 'Uninstalling'}
            </span>
          ) : deleting ? (
            'Uninstall and delete'
          ) : cascade ? (
            'Uninstall + deps'
          ) : (
            'Uninstall'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
